import { useState } from "react";
import { Zap, Instagram, Facebook, Twitter, Linkedin } from "lucide-react";
import { navLinks, legalContent } from "../../data/content";
import { usePublicData } from "../../context/PublicDataContext";
import { LegalModal } from "./LegalModal";

const iconMap = { Instagram, Facebook, Twitter, LinkedIn: Linkedin };

export const Footer = () => {
  const { content, settings } = usePublicData();
  const socialLinks = content.socialLinks || [];
  const [legal, setLegal] = useState(null);
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="bg-[#0b1221] text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2 mb-5">
              <span className="w-9 h-9 rounded-xl bg-[#0033FF] flex items-center justify-center text-white">
                <Zap className="w-5 h-5" fill="currentColor" />
              </span>
              <span className="font-display font-extrabold text-xl tracking-tight">
                QuickDrop<span className="text-[#6b8bff]"> Go</span>
              </span>
            </div>
            <p className="text-white/60 max-w-sm leading-relaxed">
              Everything you need, delivered fast. Groceries, food, gifts and
              more from your favourite local stores in Agartala, Tripura.
            </p>
            <div className="flex gap-3 mt-6">
              {socialLinks.map((s) => {
                const Icon = iconMap[s.label];
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#0033FF] hover:-translate-y-0.5 transition-[background-color,transform] duration-200"
                    data-testid={`social-${s.label.toLowerCase()}`}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-display font-bold text-sm uppercase tracking-widest text-white/40 mb-5">
              Explore
            </h4>
            <ul className="space-y-3">
              {navLinks.map((l) => (
                <li key={l.href}>
                  <button
                    onClick={() => scrollTo(l.href)}
                    className="text-white/70 hover:text-white transition-colors duration-200"
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-display font-bold text-sm uppercase tracking-widest text-white/40 mb-5">
              Company
            </h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollTo("#why")}
                  className="text-white/70 hover:text-white transition-colors duration-200"
                  data-testid="footer-company-about"
                >
                  About
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollTo("#contact")}
                  className="text-white/70 hover:text-white transition-colors duration-200"
                  data-testid="footer-company-contact"
                >
                  Contact
                </button>
              </li>
              <li>
                <a
                  href="/privacy-policy"
                  className="text-white/70 hover:text-white transition-colors duration-200"
                  data-testid="footer-company-privacy"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <button
                  onClick={() => setLegal(legalContent.terms)}
                  className="text-white/70 hover:text-white transition-colors duration-200"
                  data-testid="footer-company-terms"
                >
                  Terms &amp; Conditions
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/50">
          <p>© {new Date().getFullYear()} QuickDrop Go. All rights reserved.</p>
          <p>Made with care in Agartala, Tripura.</p>
        </div>
      </div>
      <LegalModal
        open={!!legal}
        onOpenChange={(o) => !o && setLegal(null)}
        content={legal}
      />
    </footer>
  );
};
